import { Fragment, useMemo, useState, type FC } from 'react'
import UpdateIcon from '../../assets/images/update_rg_b_48_def.png'
import ChevronIcon from '../../assets/images/arrow_chevron_right_rg_b_72_def.png'
import type {
  ChargingFeedbackMessage,
  TelemetryDataEntry,
  VehicleTelemetryMessage,
} from '../../types/charging-steering.types'
import type { AppError } from '../../types/error.types'
import {
  getTelemetryMaxFromValue,
  getTelemetryMinFromValue,
} from '../../features/chargingSteering/utils/telemetryDate.utils'
import { InfoBlock } from '../InfoBlock/InfoBlock'
import { LoadingIndicator } from '../LoadingIndicator/LoadingIndicator'

type TelemetryMessagesPanelProps = {
  from: string
  messages: VehicleTelemetryMessage[]
  loading: boolean
  error?: AppError
  feedbackMessages?: ChargingFeedbackMessage[]
  feedbackLoading?: boolean
  feedbackError?: AppError
  isVinReady: boolean
  onChangeFrom: (value: string) => void
  onCommitFrom: (value: string) => void
  onRefresh: () => void
}

type TelemetryTableRow = {
  kind: 'telemetry'
  id: string
  message: VehicleTelemetryMessage
  entry: TelemetryDataEntry
  presentation: TelemetryValuePresentation
}

type ChargingFeedbackTableRow = {
  kind: 'chargingFeedback'
  id: string
  message: ChargingFeedbackMessage
}

type MessageFeedTableRow = TelemetryTableRow | ChargingFeedbackTableRow

type TelemetryValuePresentation = {
  summary: string
  detail: TelemetryValueDetail
}

type TelemetryValueDetail =
  | { kind: 'none' }
  | { kind: 'json'; value: unknown }
  | { kind: 'departureTimes'; timers: DepartureTimer[] }
  | { kind: 'chargingTimeWindow'; window: ChargingTimeWindow }

type DepartureTimer = {
  time: string
  active: string
  days: string
  clima: string
  dcCharging: string
}

type ChargingTimeWindow = {
  start: string
  end: string
  enabled: string
}

const DEPARTURE_TIME_KEY = 'departureTime_gen6'
const CHARGING_TIME_WINDOW_KEY = 'charging_time_window'
const EMPTY_CHARGING_FEEDBACK_MESSAGES: ChargingFeedbackMessage[] = []

const DEPARTURE_DAY_FIELDS = [
  { key: 'departureTime_isMonday_gen6', label: 'Monday' },
  { key: 'departureTime_isTuesday_gen6', label: 'Tuesday' },
  { key: 'departureTime_isWednesday_gen6', label: 'Wednesday' },
  { key: 'departureTime_isThursday_gen6', label: 'Thursday' },
  { key: 'departureTime_isFriday_gen6', label: 'Friday' },
  { key: 'departureTime_isSaturday_gen6', label: 'Saturday' },
  { key: 'departureTime_isSunday_gen6', label: 'Sunday' },
]

const TELEMETRY_DATE_TIME_FORMATTER = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
})

export const TelemetryMessagesPanel: FC<TelemetryMessagesPanelProps> = ({
  from,
  messages,
  loading,
  error,
  feedbackMessages,
  feedbackLoading = false,
  feedbackError,
  isVinReady,
  onChangeFrom,
  onCommitFrom,
  onRefresh,
}) => {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({})
  const chargingFeedbackMessages = feedbackMessages ?? EMPTY_CHARGING_FEEDBACK_MESSAGES
  const includesChargingFeedback = feedbackMessages !== undefined

  const rows = useMemo(() => {
    const telemetryRows: TelemetryTableRow[] = messages.flatMap((message, messageIndex) =>
      message.data.map((entry, entryIndex) => ({
        kind: 'telemetry',
        id: createTelemetryRowId(message, messageIndex, entry, entryIndex),
        message,
        entry,
        presentation: getValuePresentation(entry),
      })),
    )
    const chargingFeedbackRows: ChargingFeedbackTableRow[] = chargingFeedbackMessages.map(
      (message, messageIndex) => ({
        kind: 'chargingFeedback',
        id: createChargingFeedbackRowId(message, messageIndex),
        message,
      }),
    )

    return [...telemetryRows, ...chargingFeedbackRows].sort(
      (left, right) => getRowTimestampInMilliseconds(right) - getRowTimestampInMilliseconds(left),
    )
  }, [chargingFeedbackMessages, messages])
  const isLoading = loading || feedbackLoading

  const handleToggleRow = (rowId: string) => {
    setExpandedRows((current) => ({
      ...current,
      [rowId]: !current[rowId],
    }))
  }

  return (
    <section className="charging-plan-editor-wrapper telemetry-messages-wrapper">
      <div className="telemetry-messages-header">
        <div className="telemetry-messages-title">
          <h2>{includesChargingFeedback ? 'Message Feed' : 'Telemetry Messages'}</h2>
          <p className="telemetry-messages-count">
            {includesChargingFeedback
              ? formatMessageCount(
                  rows.length - chargingFeedbackMessages.length,
                  messages.length,
                  chargingFeedbackMessages.length,
                )
              : formatTelemetryCount(rows.length, messages.length)}
          </p>
        </div>
        <button
          type="button"
          className="calculation-refresh-button"
          aria-label={`Refresh ${includesChargingFeedback ? 'Message Feed' : 'Telemetry Messages'}`}
          onClick={onRefresh}
          disabled={!isVinReady || isLoading}
        >
          {isLoading ? (
            <LoadingIndicator style="INLINE" />
          ) : (
            <img src={UpdateIcon} alt="" className="calculation-refresh-icon" />
          )}
        </button>
      </div>

      <div className="telemetry-controls">
        <label className="charging-plan-field telemetry-from-field">
          <span>From</span>
          <input
            type="datetime-local"
            className="charging-plan-input telemetry-from-input"
            value={from}
            min={getTelemetryMinFromValue()}
            max={getTelemetryMaxFromValue()}
            onChange={(event) => onChangeFrom(event.target.value)}
            onBlur={(event) => onCommitFrom(event.target.value)}
            disabled={!isVinReady}
          />
        </label>
      </div>

      {error && (
        <div className="telemetry-message-state">
          <InfoBlock title="Telemetry messages error" description={error.message} />
        </div>
      )}

      {feedbackError && (
        <div className="telemetry-message-state">
          <InfoBlock title="Charging feedback error" description={feedbackError.message} />
        </div>
      )}

      {isLoading && rows.length === 0 ? (
        <div className="telemetry-message-state">
          <LoadingIndicator />
        </div>
      ) : rows.length > 0 ? (
        <div className="telemetry-table-scroll">
          <table className="telemetry-messages-table">
            <thead>
              <tr>
                <th scope="col">
                  <span className="sr-only">Details</span>
                </th>
                <th scope="col">{includesChargingFeedback ? 'Received time' : 'Message time'}</th>
                <th scope="col">{includesChargingFeedback ? 'Message type' : 'Type'}</th>
                <th scope="col">{includesChargingFeedback ? 'Reference' : 'Key'}</th>
                <th scope="col">{includesChargingFeedback ? 'Status / Value' : 'Value'}</th>
                <th scope="col">{includesChargingFeedback ? 'Context' : 'Unit'}</th>
                <th scope="col">{includesChargingFeedback ? 'Source time' : 'Date of value'}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isExpanded = expandedRows[row.id] === true
                const canExpand = canExpandRow(row)
                const detailsId = `telemetry-details-${row.id}`

                return (
                  <Fragment key={row.id}>
                    <tr>
                      <td className="telemetry-details-cell">
                        {canExpand ? (
                          <button
                            type="button"
                            className={`telemetry-details-toggle${
                              isExpanded ? ' telemetry-details-toggle-expanded' : ''
                            }`}
                            aria-expanded={isExpanded}
                            aria-controls={detailsId}
                            aria-label={`${isExpanded ? 'Hide' : 'Show'} details for ${getRowReference(row)}`}
                            onClick={() => handleToggleRow(row.id)}
                          >
                            <img src={ChevronIcon} alt="" className="telemetry-details-icon" />
                          </button>
                        ) : (
                          <span className="telemetry-no-details">-</span>
                        )}
                      </td>
                      <td>{formatDateTime(getRowTimestamp(row))}</td>
                      <td>{getRowMessageType(row)}</td>
                      <td>{getRowReference(row)}</td>
                      <td>{getRowValue(row)}</td>
                      <td>{getRowContext(row)}</td>
                      <td>{getRowSourceTime(row)}</td>
                    </tr>
                    {canExpand && (
                      <tr
                        className={`telemetry-details-row${
                          isExpanded
                            ? ' telemetry-details-row-expanded'
                            : ' telemetry-details-row-collapsed'
                        }`}
                        aria-hidden={!isExpanded}
                      >
                        <td colSpan={7}>
                          <div
                            id={detailsId}
                            className={`telemetry-details-content${
                              row.kind === 'chargingFeedback'
                                ? ' charging-feedback-details-content'
                                : ''
                            }`}
                          >
                            {renderMessageFeedDetails(row)}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="telemetry-empty-message">
          {isVinReady
            ? includesChargingFeedback
              ? 'No messages returned for the selected time range.'
              : 'No telemetry data entries returned for the selected time range.'
            : includesChargingFeedback
              ? 'Select a vehicle to load the message feed.'
              : 'Select a vehicle to load telemetry messages.'}
        </p>
      )}
    </section>
  )
}

function createTelemetryRowId(
  message: VehicleTelemetryMessage,
  messageIndex: number,
  entry: TelemetryDataEntry,
  entryIndex: number,
): string {
  return `${message.timestampISO}-${messageIndex}-${entryIndex}-${formatKey(entry.key)}`
}

function createChargingFeedbackRowId(
  message: ChargingFeedbackMessage,
  messageIndex: number,
): string {
  return `charging-feedback-${message.timestamp}-${messageIndex}-${getChargingFeedbackPlanId(message) ?? 'session'}`
}

function formatMessageCount(
  dataEntryCount: number,
  telemetryMessageCount: number,
  feedbackMessageCount: number,
): string {
  const dataEntryLabel = dataEntryCount === 1 ? 'data entry' : 'data entries'
  const telemetryMessageLabel =
    telemetryMessageCount === 1 ? 'telemetry message' : 'telemetry messages'
  const feedbackMessageLabel =
    feedbackMessageCount === 1 ? 'charging feedback message' : 'charging feedback messages'

  return `${dataEntryCount} ${dataEntryLabel} from ${telemetryMessageCount} ${telemetryMessageLabel} · ${feedbackMessageCount} ${feedbackMessageLabel}`
}

function formatTelemetryCount(dataEntryCount: number, messageCount: number): string {
  const dataEntryLabel = dataEntryCount === 1 ? 'data entry' : 'data entries'
  const messageLabel = messageCount === 1 ? 'telemetry message' : 'telemetry messages'

  return `${dataEntryCount} ${dataEntryLabel} from ${messageCount} ${messageLabel}`
}

function getRowTimestampInMilliseconds(row: MessageFeedTableRow): number {
  const timestamp = new Date(getRowTimestamp(row)).getTime()
  return Number.isNaN(timestamp) ? 0 : timestamp
}

function getRowTimestamp(row: MessageFeedTableRow): string {
  return row.kind === 'telemetry' ? row.message.timestampISO : row.message.timestamp
}

function getRowMessageType(row: MessageFeedTableRow): string {
  return row.kind === 'telemetry' ? formatTelemetryType(row.message.type) : 'Charging feedback'
}

function getRowReference(row: MessageFeedTableRow): string {
  if (row.kind === 'telemetry') {
    return formatKey(row.entry.key)
  }

  return getChargingFeedbackPlanId(row.message) ?? 'Session event'
}

function getRowValue(row: MessageFeedTableRow): string {
  if (row.kind === 'telemetry') {
    return row.presentation.summary
  }

  return getChargingFeedbackStatus(row.message) ?? 'Feedback received'
}

function getRowContext(row: MessageFeedTableRow): string {
  if (row.kind === 'telemetry') {
    return formatOptionalText(row.entry.unit)
  }

  return getChargingFeedbackSessionState(row.message) ?? '-'
}

function getRowSourceTime(row: MessageFeedTableRow): string {
  return row.kind === 'telemetry' ? formatDateTime(row.entry.date_of_value) : '-'
}

function canExpandRow(row: MessageFeedTableRow): boolean {
  return row.kind === 'chargingFeedback' || row.presentation.detail.kind !== 'none'
}

function renderMessageFeedDetails(row: MessageFeedTableRow) {
  return row.kind === 'telemetry'
    ? renderTelemetryDetails(row)
    : renderChargingFeedbackDetails(row.message)
}

function renderChargingFeedbackDetails(message: ChargingFeedbackMessage) {
  const details = [
    { label: 'Plan ID', value: getChargingFeedbackPlanId(message) ?? '-' },
    { label: 'Status', value: getChargingFeedbackStatus(message) ?? '-' },
    { label: 'Reason', value: getChargingFeedbackReason(message) ?? '-' },
    { label: 'Session state', value: getChargingFeedbackSessionState(message) ?? '-' },
  ]

  return (
    <>
      <h3 className="telemetry-details-title">Charging feedback</h3>
      <div className="charging-feedback-details-grid">
        {details.map(({ label, value }) => (
          <div className="charging-feedback-details-item" key={label}>
            <span className="charging-feedback-details-label">{label}</span>
            <span className="charging-feedback-details-value">{value}</span>
          </div>
        ))}
      </div>
      <h4 className="charging-feedback-details-section-title">Raw message</h4>
      <pre className="telemetry-json-details">{formatJsonValue(message)}</pre>
    </>
  )
}

function getChargingFeedbackPlanId(message: ChargingFeedbackMessage): string | undefined {
  const feedback = getChargingStatusFeedback(message)

  return (
    (feedback ? readNonEmptyString(feedback, 'id') : undefined) ??
    readNonEmptyString(message, 'chargingPlanId') ??
    readNonEmptyString(message, 'planId')
  )
}

function getChargingFeedbackStatus(message: ChargingFeedbackMessage): string | undefined {
  const feedback = getChargingStatusFeedback(message)
  return feedback ? readNonEmptyString(feedback, 'status') : undefined
}

function getChargingFeedbackReason(message: ChargingFeedbackMessage): string | undefined {
  const feedback = getChargingStatusFeedback(message)
  return feedback ? readNonEmptyString(feedback, 'reason') : undefined
}

function getChargingFeedbackSessionState(message: ChargingFeedbackMessage): string | undefined {
  return readNonEmptyString(message, 'sessionState')
}

function getChargingStatusFeedback(
  message: ChargingFeedbackMessage,
): Record<string, unknown> | undefined {
  return isRecord(message.chargingStatusFeedback) ? message.chargingStatusFeedback : undefined
}

function readNonEmptyString(record: Record<string, unknown>, key: string): string | undefined {
  const value = record[key]

  if (typeof value !== 'string') {
    return undefined
  }

  const trimmedValue = value.trim()
  return trimmedValue.length > 0 ? trimmedValue : undefined
}

function getValuePresentation(entry: TelemetryDataEntry): TelemetryValuePresentation {
  if (entry.key === DEPARTURE_TIME_KEY) {
    const timers = parseDepartureTimers(entry.value)

    if (timers) {
      const suffix = timers.length === 1 ? 'departure time' : 'departure times'
      return {
        summary: `${timers.length} ${suffix}`,
        detail: { kind: 'departureTimes', timers },
      }
    }
  }

  if (entry.key === CHARGING_TIME_WINDOW_KEY) {
    const window = parseChargingTimeWindow(entry.value)

    if (window) {
      return {
        summary: formatChargingTimeWindowSummary(window),
        detail: { kind: 'chargingTimeWindow', window },
      }
    }
  }

  if (isComplexValue(entry.value)) {
    return {
      summary: summarizeComplexValue(entry.value),
      detail: { kind: 'json', value: entry.value },
    }
  }

  return {
    summary: formatDisplayValue(entry.value),
    detail: { kind: 'none' },
  }
}

function parseChargingTimeWindow(value: unknown): ChargingTimeWindow | undefined {
  if (!isRecord(value)) {
    return undefined
  }

  return {
    start: formatTimeWindowBoundary(value.start),
    end: formatTimeWindowBoundary(value.end),
    enabled: formatDisplayValue(value.enabled),
  }
}

function formatChargingTimeWindowSummary(window: ChargingTimeWindow): string {
  const status =
    window.enabled === 'Yes' ? 'Enabled' : window.enabled === 'No' ? 'Disabled' : window.enabled

  return `${status}, ${window.start} - ${window.end}`
}

function formatTimeWindowBoundary(value: unknown): string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    return '-'
  }

  const timeMatch = value.match(/T(\d{2}):(\d{2})/)

  if (timeMatch) {
    return `${timeMatch[1]}:${timeMatch[2]}`
  }

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) {
    return '-'
  }

  return `${date.getUTCHours().toString().padStart(2, '0')}:${date
    .getUTCMinutes()
    .toString()
    .padStart(2, '0')}`
}

function parseDepartureTimers(value: unknown): DepartureTimer[] | undefined {
  if (!Array.isArray(value)) {
    return undefined
  }

  const timers: DepartureTimer[] = []

  for (const timerGroup of value) {
    if (!Array.isArray(timerGroup) || !timerGroup.every(isTelemetryField)) {
      return undefined
    }

    timers.push({
      time: formatDepartureTime(timerGroup),
      active: formatDisplayValue(readTimerFieldValue(timerGroup, 'departureTime_isActive_gen6')),
      days: formatDepartureDays(timerGroup),
      clima: formatDisplayValue(
        readTimerFieldValue(timerGroup, 'departureTime_clima_precon_active_gen6'),
      ),
      dcCharging: formatDisplayValue(
        readTimerFieldValue(timerGroup, 'departureTime_dc_charging_precon_active_gen6'),
      ),
    })
  }

  return timers
}

function formatDepartureTime(timerGroup: TelemetryDataEntry[]): string {
  const hour = readTimerFieldValue(timerGroup, 'departureTime_hour_gen6')
  const minute = readTimerFieldValue(timerGroup, 'departureTime_minute_gen6')

  if (hour === undefined || minute === undefined) {
    return '-'
  }

  const hourNumber = Number(hour)
  const minuteNumber = Number(minute)

  if (!Number.isInteger(hourNumber) || !Number.isInteger(minuteNumber)) {
    return '-'
  }

  return `${hourNumber.toString().padStart(2, '0')}:${minuteNumber.toString().padStart(2, '0')}`
}

function formatDepartureDays(timerGroup: TelemetryDataEntry[]): string {
  const activeDays = DEPARTURE_DAY_FIELDS.filter(({ key }) =>
    isTruthyTimerValue(readTimerFieldValue(timerGroup, key)),
  ).map(({ label }) => label)

  return activeDays.length > 0 ? activeDays.join(', ') : 'none'
}

function readTimerFieldValue(timerGroup: TelemetryDataEntry[], fieldKey: string): unknown {
  return timerGroup.find((field) => field.key === fieldKey)?.value
}

function isTruthyTimerValue(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value
  }

  if (typeof value === 'number') {
    return value === 1
  }

  if (typeof value === 'string') {
    return value.toLowerCase() === 'true' || value === '1'
  }

  return false
}

function renderTelemetryDetails(row: TelemetryTableRow) {
  if (row.presentation.detail.kind === 'departureTimes') {
    return (
      <>
        <h3 className="telemetry-details-title">{formatKey(row.entry.key)}</h3>
        {row.presentation.detail.timers.length > 0 ? (
          <div className="telemetry-departure-table-scroll">
            <table className="telemetry-departure-table">
              <thead>
                <tr>
                  <th scope="col">Timer</th>
                  <th scope="col">Time</th>
                  <th scope="col">Active</th>
                  <th scope="col">Days</th>
                  <th scope="col">Climate</th>
                  <th scope="col">DC charging</th>
                </tr>
              </thead>
              <tbody>
                {row.presentation.detail.timers.map((timer, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{timer.time}</td>
                    <td>{timer.active}</td>
                    <td>{timer.days}</td>
                    <td>{timer.clima}</td>
                    <td>{timer.dcCharging}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>No departure timers in this value.</p>
        )}
      </>
    )
  }

  if (row.presentation.detail.kind === 'chargingTimeWindow') {
    const window = row.presentation.detail.window

    return (
      <>
        <h3 className="telemetry-details-title">{formatKey(row.entry.key)}</h3>
        <div className="telemetry-departure-table-scroll">
          <table className="telemetry-departure-table">
            <thead>
              <tr>
                <th scope="col">Start</th>
                <th scope="col">End</th>
                <th scope="col">Enabled</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{window.start}</td>
                <td>{window.end}</td>
                <td>{window.enabled}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </>
    )
  }

  if (row.presentation.detail.kind === 'json') {
    return (
      <>
        <h3 className="telemetry-details-title">{formatKey(row.entry.key)}</h3>
        <pre className="telemetry-json-details">
          {formatJsonValue(row.presentation.detail.value)}
        </pre>
      </>
    )
  }

  return null
}

function isTelemetryField(value: unknown): value is TelemetryDataEntry {
  return isRecord(value) && typeof value.key === 'string'
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isComplexValue(value: unknown): boolean {
  return Array.isArray(value) || isRecord(value)
}

function summarizeComplexValue(value: unknown): string {
  if (Array.isArray(value)) {
    if (value.every(Array.isArray)) {
      const suffix = value.length === 1 ? 'group' : 'groups'
      return `Array with ${value.length} ${suffix}`
    }

    const suffix = value.length === 1 ? 'entry' : 'entries'
    return `${value.length} ${suffix}`
  }

  if (isRecord(value)) {
    return 'Complex value'
  }

  return formatPrimitiveValue(value)
}

function formatPrimitiveValue(value: unknown): string {
  return formatDisplayValue(value)
}

function formatDisplayValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '-'
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim()

    if (trimmedValue.length === 0) {
      return '-'
    }

    if (trimmedValue.toLowerCase() === 'true') {
      return 'Yes'
    }

    if (trimmedValue.toLowerCase() === 'false') {
      return 'No'
    }

    if (trimmedValue === 'ACTIVE') {
      return 'Active'
    }

    if (trimmedValue === 'INACTIVE') {
      return 'Inactive'
    }

    return trimmedValue
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }

  if (typeof value === 'number' || typeof value === 'bigint') {
    return String(value)
  }

  return '-'
}

function formatTelemetryType(value: string): string {
  switch (value) {
    case 'data_update':
      return 'Data update'
    case 'geofence_enter':
      return 'Geofence enter'
    case 'geofence_left':
      return 'Geofence left'
    default:
      return value
  }
}

function formatOptionalText(value: string | null | undefined): string {
  return value && value.trim().length > 0 ? value : '-'
}

function formatKey(value: string | undefined): string {
  return value && value.trim().length > 0 ? value : '-'
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return '-'
  }

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) {
    return '-'
  }

  return TELEMETRY_DATE_TIME_FORMATTER.format(date)
}

function formatJsonValue(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2) ?? '-'
  } catch {
    return '-'
  }
}
